import firebase from "firebase/compat/app";
import Account from "../app/models/Account";
import { Collaboration, Room } from "../app/models/Chat";
import Message from "../app/models/Message";

import * as lc from "../app/modules/localstorage";
import {
  MessageSendingService,
  CollaborationService,
} from "../app/modules/chat/services";

// Re-export media utilities from new module location
export {
  saveMessageMedia,
  getMediaUrl,
  getMediaFilename,
  getMediaMimeType,
  getMediaExtension,
  getMediaDuration,
  getFilenameWithExtension,
  hasMedia,
} from "../app/modules/chat/services";

// Re-export CollaborationService for new consumers
export { CollaborationService } from "../app/modules/chat/services";

// ============================================================================
// ROOM & COLLABORATION FETCH FUNCTIONS
// Deprecated: Use CollaborationService methods directly
// ============================================================================

/**
 * @deprecated Use CollaborationService.fetchRoomsByCollaborationId() instead
 */
export const fetchRoomByCollaborationId = async (collaborationId: string) => {
  return CollaborationService.fetchRoomsByCollaborationId(collaborationId);
};

/**
 * @deprecated Use CollaborationService.fetchCollaborationById() instead
 */
export const fetchCollaborationByID = async (collaborationId: string) => {
  return CollaborationService.fetchCollaborationById(collaborationId);
};

/**
 * @deprecated Use CollaborationService.fetchCollaborationByUserSearchKeys() instead
 */
export const fetchCollaborationByUserSearchKeys = async (
  marketplace: string,
  userSearchKey: string
) => {
  return CollaborationService.fetchCollaborationByUserSearchKeys(
    marketplace,
    userSearchKey
  );
};

/**
 * @deprecated Use CollaborationService.fetchQueuedCollaborationById() instead
 */
export const fetchQueuedCollaborationByID = async (collabId: string) => {
  return CollaborationService.fetchQueuedCollaborationById(collabId);
};

/**
 * @deprecated Use CollaborationService.updateCollaborationCustomer() instead
 */
export const updateCollaborationCustomer = async (
  collab: Collaboration,
  customerId: string
) => {
  return CollaborationService.updateCollaborationCustomer(collab, customerId);
};

// ============================================================================
// COLLABORATION LIST FETCH FUNCTIONS
// Deprecated: Use CollaborationService methods directly
// ============================================================================

/**
 * @deprecated Use CollaborationService.fetchMyChats() instead
 */
export const fetchCollaborations = (uid: string, clientid: string) => {
  return CollaborationService.fetchMyChats(uid, clientid);
};

/**
 * @deprecated Use CollaborationService.fetchAllChats() instead
 */
export const fetchCollaborationsByAllCompany = (
  companies: Array<firebase.firestore.DocumentReference>,
  useruid: string
) => {
  return CollaborationService.fetchAllChats(companies as any, useruid);
};

// ============================================================================
// MESSAGE FETCH FUNCTIONS
// Deprecated: Use CollaborationService methods directly
// ============================================================================

/**
 * @deprecated Use CollaborationService.fetchMessages() instead
 */
export const fetchMessageCollaboration = async (
  listMessages: Message[],
  collaborationid: string,
  roomsid: string,
  lastMessage: any,
  selectedChat: string,
  callback: any
) => {
  return CollaborationService.fetchMessages(
    listMessages,
    collaborationid,
    roomsid,
    lastMessage,
    selectedChat,
    callback
  );
};

/**
 * @deprecated Use CollaborationService.fetchPreviousMessages() instead
 */
export const fetchCollaborationPrevMessage = (
  collaborationid: string,
  lastMessage: any,
  callback: any,
  roomid?: string
) => {
  return CollaborationService.fetchPreviousMessages(
    collaborationid,
    lastMessage,
    callback,
    roomid
  );
};

// ============================================================================
// MESSAGE SENDING (Delegated to MessageSendingService)
// ============================================================================

export const createCollaborationMessage = async (
  newMessage: Message,
  companyID: string,
  selectedChat: string,
  account: Account | undefined,
  customer: any,
  callback: any,
  isRetry: boolean,
  roomsModel?: Room[]
) => {
  if (!newMessage) return;

  // Convert to updatable object
  let msg: Message = JSON.parse(JSON.stringify(newMessage));
  msg.collaboration = newMessage.collaboration;
  msg.room = newMessage.room;
  if (newMessage.user) msg.user = newMessage.user;
  msg.client = newMessage.client as firebase.firestore.DocumentReference;
  if (newMessage.customer)
    msg.customer = newMessage.customer as firebase.firestore.DocumentReference;
  msg.createdAt = newMessage.createdAt;
  msg.updatedAt = newMessage.updatedAt;
  if (msg.replyMessageID)
    msg.replyMessageID =
      newMessage.replyMessageID as firebase.firestore.DocumentReference;

  const room = roomsModel?.find((r) => r.id === msg.room.id);

  // Delegate to MessageSendingService
  await MessageSendingService.sendMessage({
    message: msg,
    companyID,
    account,
    room,
    callback,
    isRetry,
  });
};

// ============================================================================
// MESSAGE UTILITY FUNCTIONS
// Deprecated: Use CollaborationService methods directly
// ============================================================================

/**
 * @deprecated Use CollaborationService.clearUnreadMessages() instead
 */
export const clearUnreadMessages = async (collaborationId: string) => {
  return CollaborationService.clearUnreadMessages(collaborationId);
};

/**
 * @deprecated Use CollaborationService.clearUnreadMessagesAllChat() instead
 */
export const clearUnreadMessagesAllChat = async (collaborationId: string) => {
  return CollaborationService.clearUnreadMessagesAllChat(collaborationId);
};

/**
 * @deprecated Use CollaborationService.clearUserUnreadMessages() instead
 */
export const clearUserUnreadMessages = async (collaborationId: string) => {
  return CollaborationService.clearUserUnreadMessages(collaborationId);
};

/**
 * @deprecated Use CollaborationService.updateMessageRead() instead
 */
export const updateMessageRead = async (newMessage: Message) => {
  return CollaborationService.updateMessageRead(newMessage);
};

export const requestMarkAsReadMessage = async (jsonRequest: any) => {
  const server = await import("../api/server/message");
  server.sendRequestMarkAsReadMessage(jsonRequest);
};

// ============================================================================
// COLLABORATION UPDATE FUNCTIONS
// Deprecated: Use CollaborationService methods directly
// ============================================================================

/**
 * @deprecated Use CollaborationService.updateLastInteraction() instead
 */
export const updateLastInteraction = async (
  collaborationid: string,
  newMessage: Message,
  roomId?: string
) => {
  return CollaborationService.updateLastInteraction(
    collaborationid,
    newMessage,
    roomId
  );
};

/**
 * @deprecated Use CollaborationService.updateLastInteractionUserChat() instead
 */
export const updateLastInteractionUserChat = async (
  collaborationid: string,
  newMessage: Message
) => {
  return CollaborationService.updateLastInteractionUserChat(
    collaborationid,
    newMessage
  );
};

// ============================================================================
// FRIEND COLLABORATION FUNCTIONS
// Deprecated: Use CollaborationService methods directly
// ============================================================================

/**
 * @deprecated Use CollaborationService.fetchFriendCollaborations() instead
 */
export const fetchFriendCollaborations = (uid: string) => {
  return CollaborationService.fetchFriendCollaborations(uid);
};

/**
 * @deprecated Use CollaborationService.createFriendCollaborationMessage() instead
 */
export const createFriendCollaborationMessage = async (
  newMessage: Message,
  collaborationId: string,
  toUserId: string,
  callback: any
) => {
  return CollaborationService.createFriendCollaborationMessage(
    newMessage,
    collaborationId,
    toUserId,
    callback
  );
};

/**
 * @deprecated Use CollaborationService.fetchUserCollaborationById() instead
 */
export const fetchUserCollaborationByID = async (collabId: string) => {
  return CollaborationService.fetchUserCollaborationById(collabId);
};

// ============================================================================
// MARKETPLACE FUNCTIONS
// ============================================================================

export const createMessageInitiateChatMarketplace = async (
  marketplace: string,
  orderId: string
) => {
  const order = await import("../api/server/order");
  const userId = lc.getUserId();
  const result = await order.sendRequestInitiateChatMarketplace(
    marketplace,
    orderId,
    userId
  );
  return result.data;
};

export const requestReloadProductMessage = async (jsonRequest: any) => {
  const server = await import("../api/server/message");
  server.sendRequestReloadProductMessage(jsonRequest);
};
